import { useEffect, useMemo, useRef, useState } from 'react';
import { config } from '../../config';
import { loadDiscovery } from '../../app/discovery';
import type { DiscoveryHypothesisView } from '../../app/discovery';
import { confirmLock } from '../../app/lockedBoard';
import { log } from '../../app/logger';
import { suggestLockSelection, swapSelection } from '../../domain/lock';
import { discoveryOrder } from '../../domain/order';
import { PhaseIcon } from '../PhaseIcon';

/**
 * B-10 — the **lock review** (design spec §2.4; scenario 12), reached from the lock nudge in
 * the notification strip. A middle-band surface, deliberately **not** a modal dialog: §2.4
 * lists it among the takeover's middle-band surfaces, and the whistle in the top band must
 * stay reachable — a half can end while the user is choosing, and the hold must not be inert
 * behind a sheet.
 *
 * ── Nothing here decides anything ────────────────────────────────────────────
 *
 * `suggestLockSelection` is the engine's LOCK-2 — the deterministic top-`BOARD_CAP` proposal
 * (evidenced, DISC-4 order; proved deterministic over shuffled insertion orders in the engine
 * suite). It is computed **once on entry**: recomputing per render would let a count committed
 * while the user decides rearrange the eight under their thumb. `swapSelection` is LOCK-3's
 * edit — replace-at-index, so the confirmed board order is the order the user arranged — and
 * it is also scenario 12's displacement rule before the lock exists: an incoming candidate
 * enters **only** by the user naming which chosen one it replaces; there is no add-without-
 * outgoing control on a full selection anywhere on this surface. `confirmLock` dispatches
 * LOCK-4, whose handler, repo and unique index each refuse a second lock and a ninth tile.
 * This file reads, renders and taps.
 *
 * The slot meter (brand kit §6) shows `BOARD_CAP` cells with the chosen count filled — a
 * count, never `n/8` as a requirement: fewer than the cap is legal (LOCK-7) and the board
 * simply opens with empty slots.
 *
 * No typing exists on this surface: every control is a tap (the in-play no-typing rule; adds
 * happen through Discovery's gated sheet or the break hub, never here).
 */
export interface LockReviewSheetBProps {
  readonly matchId: string;
  readonly onClose: () => void;
  /** Called once LOCK-4 has committed — the shell re-reads and the board surface takes over. */
  readonly onLocked: () => void;
}

type Phase = 'loading' | 'ready' | 'failed';

const MODULE = 'ui-b/lockReview';

export function LockReviewSheetB({ matchId, onClose, onLocked }: LockReviewSheetBProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [candidates, setCandidates] = useState<readonly DiscoveryHypothesisView[]>([]);
  const [selection, setSelection] = useState<readonly string[]>([]);
  const [replacing, setReplacing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void (async () => {
      try {
        const context = await loadDiscovery(matchId);
        if (context === undefined) {
          setPhase('failed');
          return;
        }
        setCandidates(context.rows);
        setSelection(suggestLockSelection(context.rows).map((row) => row.id));
        setPhase('ready');
      } catch (cause) {
        log.exception(MODULE, 'the lock review could not be loaded', cause);
        setPhase('failed');
      }
    })();
  }, [matchId]);

  const byId = useMemo(() => new Map(candidates.map((row) => [row.id, row])), [candidates]);

  /** LOCK-3 — "any other existing hypothesis", in DISC-4's order so the strongest read first. */
  const alternatives = useMemo(
    () => discoveryOrder(candidates.filter((row) => !selection.includes(row.id))),
    [candidates, selection],
  );

  const commit = (): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    void confirmLock(matchId, selection).then(
      (result) => {
        setBusy(false);
        if (!result.ok) {
          setError(result.message);
          return;
        }
        onLocked();
      },
      (cause: unknown) => {
        setBusy(false);
        setError(cause instanceof Error ? cause.message : String(cause));
      },
    );
  };

  const swap = (incomingId: string): void => {
    if (replacing === null) return;
    const next = swapSelection(selection, replacing, incomingId);
    setReplacing(null);
    if (!next.ok) {
      setError(next.message);
      return;
    }
    setError(null);
    setSelection(next.value);
  };

  return (
    <div className="lockrev" data-testid="lockrev" role="region" aria-label="Lock the board">
      <div className="lockrev-bar">
        <h2 className="lockrev-title">Lock my {config.BOARD_CAP}</h2>
        <button type="button" className="lockrev-close" data-testid="lockrev-close" onClick={onClose}>
          Not yet
        </button>
      </div>

      {phase === 'loading' && <p className="capb-loading">Loading…</p>}
      {phase === 'failed' && (
        <p className="capb-loading" role="alert">
          The candidates could not be read. The log has the detail.
        </p>
      )}

      {phase === 'ready' && (
        <div className="lockrev-body">
          {/* The kit §6 slot meter at BOARD_CAP: chosen slots fill, the rest stay open —
              LOCK-7 makes a short board legal, so this is a count, never a requirement. */}
          <div className="lockrev-meter" data-testid="lockrev-meter" aria-hidden="true">
            {Array.from({ length: config.BOARD_CAP }, (_, index) => (
              <span
                key={index}
                className="lockrev-meter__cell"
                data-filled={index < selection.length ? 'true' : 'false'}
              />
            ))}
          </div>
          <p className="lockrev-count" data-testid="lockrev-count">
            {selection.length} of {config.BOARD_CAP} chosen — positions lock in this order.
          </p>

          {replacing === null ? (
            <>
              <ol className="lockrev-list" data-testid="lockrev-selection">
                {selection.map((id, index) => {
                  const row = byId.get(id);
                  return (
                    <li key={id} className="lockrev-row" data-hid={id}>
                      <span className="lockrev-slot">{index + 1}</span>
                      {row !== undefined && <PhaseIcon phase={row.phase} />}
                      <span className="lockrev-text">{row?.text ?? id}</span>
                      <span className="lockrev-badge">{row?.cachedCount ?? 0}</span>
                      <button
                        type="button"
                        className="lockrev-swap"
                        data-testid={`lockrev-swap-${id}`}
                        onClick={() => {
                          setReplacing(id);
                        }}
                      >
                        Swap
                      </button>
                    </li>
                  );
                })}
              </ol>
              {selection.length === 0 && (
                <p className="lockrev-empty" data-testid="lockrev-empty">
                  Nothing observed yet — tap what you see in Discovery first.
                </p>
              )}
            </>
          ) : (
            <>
              <p className="lockrev-replacing" data-testid="lockrev-replacing">
                Replacing <strong>{byId.get(replacing)?.text ?? replacing}</strong> — pick its
                replacement.
              </p>
              <ul className="lockrev-list" data-testid="lockrev-alternatives">
                {alternatives.map((row) => (
                  <li key={row.id} className="lockrev-row">
                    <button
                      type="button"
                      className="lockrev-pick"
                      data-testid={`lockrev-pick-${row.id}`}
                      onClick={() => {
                        swap(row.id);
                      }}
                    >
                      <PhaseIcon phase={row.phase} />
                      <span className="lockrev-text">{row.text}</span>
                      <span className="lockrev-badge">{row.cachedCount}</span>
                    </button>
                  </li>
                ))}
                {alternatives.length === 0 && (
                  <li className="lockrev-empty">
                    Every hypothesis on this match is already in the {config.BOARD_CAP}.
                  </li>
                )}
              </ul>
              <button
                type="button"
                className="lockrev-keep"
                data-testid="lockrev-keep"
                onClick={() => {
                  setReplacing(null);
                }}
              >
                Keep it
              </button>
            </>
          )}

          {error !== null && (
            <p className="lockrev-error" role="alert" data-testid="lockrev-error">
              {error}
            </p>
          )}

          {/* "The app never auto-locks" — this is the only control that locks anything, and it
              is a deliberate tap. Positions 0..n−1 are assigned in exactly this list's order. */}
          <button
            type="button"
            className="lockrev-confirm"
            data-testid="lockrev-confirm"
            onClick={commit}
            disabled={busy || replacing !== null || selection.length === 0}
          >
            Lock these {selection.length}
          </button>
        </div>
      )}
    </div>
  );
}
